import CleaningCommandButton, {
  ButtonApiConfiguration,
  mapButtonConfigurationApiDataToButtonList,
  mapPartialButtonConfigurationToApi,
} from "../buttons/command-buttons";
import {
  CleaningConfiguration,
  CleaningConfigurationApi,
  mapCleaningConfigurationApiToCleaningConfiguration,
  mapPartialCleaningConfigurationApiToCleaningConfiguration,
  mapPartialCleaningConfigurationToApi,
} from "../cleaning-configurations/global-cleaning-configuration";
import Station from "./station";
import { CleaningZoneApi } from "./cleaning-zone";
import { TeachAndRepeatZonePathApi } from "./teach-and-repeat-zone-path";
import { RedZoneApi } from "./red-zone";
import { RuleZoneApi } from "./rule-zone";
import { LandmarkApi } from "./landmark";
import { MissionApiData } from "../missions/mission";
import { RoomLimitZoneApi } from "./room-limit-zone";
import { GateApi } from "./gate";
import { WaypointEdge, WaypointNodeApi } from "./waypoints";
import DeepPartial from "../../../utils/deep-partial";
import MapInfo from "../../cleaning-map/map-info";
import { mapMapRulesConfigurationApiToMapRulesConfiguration, mapPartialMapRulesConfigurationToApi, MapRulesConfiguration, MapRulesConfigurationApi } from "../cleaning-configurations/map-rules-configuration";

export interface MapInformation {
  id?: string;
  width: number;
  height: number;
  resolution: number;
  xPosition: number;
  yPosition: number;
}

interface RoomData {
  id: string;
  floorId: string;
  name: string;
  isArchived: boolean;

  hasPosegraphAndDataFiles: boolean;

  map: string | null;
  mapInformation: MapInformation;
  fleetControlId: string | null;

  primaryStation: Station;
  secondaryStation: Station | null;

  cleaningConfiguration: CleaningConfiguration;
  mapRulesConfiguration: MapRulesConfiguration;
  buttonConfiguration: CleaningCommandButton[];
}

export interface RoomApiData {
  id: string;
  floor: string;
  name: string;
  isArchived: boolean;

  hasPosegraphAndDataFiles: boolean;

  map: string | null;
  mapInformation: MapInformation;
  fleetControlId: string | null;

  primaryStation: Station;
  secondaryStation: Station | null;

  cleaningConfiguration: CleaningConfigurationApi;
  mapRulesConfiguration: MapRulesConfigurationApi;
  buttonConfiguration: ButtonApiConfiguration;

  cleaningZones: CleaningZoneApi[];
  teachAndRepeat: TeachAndRepeatZonePathApi[];
  redZones: RedZoneApi[];
  ruleZones: RuleZoneApi[];
  landmarks: LandmarkApi[];
  missions: MissionApiData[];
  gates: GateApi[];
  waypointNodes: WaypointNodeApi[];
  waypointEdges: WaypointEdge[];
  roomLimitZones: RoomLimitZoneApi[];
}

export const isRoomMapDataValid = (room: RoomData): boolean => {
  return (
    room.mapInformation.width != null &&
    room.mapInformation.height != null &&
    room.mapInformation.resolution != null &&
    room.mapInformation.xPosition != null &&
    room.mapInformation.yPosition != null
  );
};

export default RoomData;

export const mapRoomApiDataToRoomData = (apiData: RoomApiData) => ({
  ...apiData,
  floorId: apiData.floor,
  cleaningConfiguration: mapCleaningConfigurationApiToCleaningConfiguration(apiData.cleaningConfiguration),
  mapRulesConfiguration: mapMapRulesConfigurationApiToMapRulesConfiguration(apiData.mapRulesConfiguration),
  buttonConfiguration: mapButtonConfigurationApiDataToButtonList(apiData.buttonConfiguration),
});

export const mapPartialApiToRoom = (apiData: Partial<RoomApiData>) => ({
  ...apiData,
  floorId: apiData.floor,
  cleaningConfiguration:
    apiData.cleaningConfiguration != null
      ? mapPartialCleaningConfigurationApiToCleaningConfiguration(apiData.cleaningConfiguration)
      : undefined,
  mapRulesConfiguration:
    apiData.mapRulesConfiguration != null
      ? mapMapRulesConfigurationApiToMapRulesConfiguration(apiData.mapRulesConfiguration)
      : undefined,
  buttonConfiguration:
    apiData.buttonConfiguration != null
      ? mapButtonConfigurationApiDataToButtonList(apiData.buttonConfiguration)
      : undefined,
});

export const mapPartialRoomToApi = (roomData: DeepPartial<RoomData>) => {
  const { floorId, ...data } = roomData; // I need destructuring to avoid passing both floor and floorId
  return {
    ...data,
    floor: floorId,
    buttonConfiguration:
      roomData.buttonConfiguration != null
        ? mapPartialButtonConfigurationToApi(roomData.buttonConfiguration)
        : undefined,
    cleaningConfiguration:
      roomData.cleaningConfiguration != null
        ? mapPartialCleaningConfigurationToApi(roomData.cleaningConfiguration)
        : undefined,
    mapRulesConfiguration:
      roomData.mapRulesConfiguration != null
        ? mapPartialMapRulesConfigurationToApi(roomData.mapRulesConfiguration)
        : undefined,
  } as DeepPartial<RoomApiData>;
}

export const getMapInfoFromRoomMapData = (mapData: MapInformation): MapInfo => {
  return {
    originX: mapData.xPosition,
    originY: mapData.yPosition,
    width: mapData.width,
    height: mapData.height,
    resolution: mapData.resolution,
    map: mapData.id ?? "",
  };
};